import React, { useState, useEffect } from 'react';
import { TailwindButton } from '../Tailwind';


export interface UserClaimWriteProps {
  userRights: {
    user_id: number;
    right_to_write: boolean;
  };
  userEditing: {
    user_id: number;
    user_name: string;
  };
  userClaimingWrite: {
    user_id: number;
    user_name: string;
  };
  claimWriteRequested: boolean;
  readOnlyReason?: string | null;
  onClaimWrite: () => void;
  onGiveWrite: () => void;
}

export interface FMEStatusBarProps {
  isComputing?: boolean;
  isComputed?: boolean;
  auditPassed?: boolean | null;
  isConnected?: boolean;
  firstError?: string | null;
  onErrorClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  sensitivityErrorCount?: number;
  onSensitivityErrorClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  claimWrite?: UserClaimWriteProps;
}

export default function FMEStatusBar({
  isComputing = false,
  isComputed,
  auditPassed,
  isConnected = true,
  firstError = null,
  onErrorClick,
  sensitivityErrorCount = 0,
  onSensitivityErrorClick,
  claimWrite,
}: FMEStatusBarProps) {
  // isComputed === undefined means the model status is still loading — show nothing
  // rather than flashing a misleading "Start building your model" message.
  const statusKnown = isComputed !== undefined;

  // Debounce disconnection to avoid flashing "Connection lost" during scenario switches
  const [showDisconnected, setShowDisconnected] = useState(false);
  useEffect(() => {
    if (isConnected) {
      setShowDisconnected(false);
      return;
    }
    const timer = setTimeout(() => setShowDisconnected(true), 1500);
    return () => clearTimeout(timer);
  }, [isConnected]);

  return (
    <div className="flex items-center gap-3 text-xs font-medium h-7 w-full">
      {/* Connection lost banner — debounced to avoid flash on scenario switch */}
      {showDisconnected && (
        <span className="flex items-center gap-1.5 text-red-500">
          <span className="material-symbols-outlined text-[16px]">cloud_off</span>
          Connection lost — reconnecting...
        </span>
      )}

      {/* Scenario error button */}
      {firstError && (
        <TailwindButton
          variant="secondary"
          size="sm"
          icon="troubleshoot"
          onClick={onErrorClick}
          className="h-7 text-xs !bg-orange-50 !border-orange-200 !text-orange-600 hover:!bg-orange-100 hover:!border-orange-300 hover:!text-orange-700"
        >
          Incomplete Scenario
        </TailwindButton>
      )}

      {/* Sensitivity error button — separate from scenario errors */}
      {sensitivityErrorCount > 0 && (
        <TailwindButton
          variant="secondary"
          size="sm"
          icon="troubleshoot"
          onClick={onSensitivityErrorClick}
          className="h-7 text-xs !bg-orange-50 !border-orange-200 !text-orange-600 hover:!bg-orange-100 hover:!border-orange-300 hover:!text-orange-700"
        >
          Incomplete Sensitivity
        </TailwindButton>
      )}

      {/* Computing indicator - shown next to error button if both apply */}
      {isComputing && (
        <span className="flex items-center gap-1.5 text-slate-500">
          <span className="material-symbols-outlined animate-spin text-[16px]">
            progress_activity
          </span>
          Calculating Model...
        </span>
      )}

      {!isComputing && !firstError && sensitivityErrorCount === 0 && isComputed && (
        <span className="flex items-center gap-1.5 text-emerald-600">
          <span className="material-symbols-outlined text-[16px]">check_circle</span>
          Model Calculated and Complete
        </span>
      )}

      {/* Audit status indicator - hidden when there are any errors */}
      {!isComputing && !firstError && sensitivityErrorCount === 0 && isComputed && auditPassed === true && (
        <span className="flex items-center gap-1.5 text-emerald-600">
          <span className="material-symbols-outlined text-[16px]">check_circle</span>
          Audit tests passed
        </span>
      )}
      {!isComputing && !firstError && sensitivityErrorCount === 0 && isComputed && auditPassed === false && (
        <span className="flex items-center gap-1.5 text-orange-600">
          <span className="material-symbols-outlined text-[16px]">error</span>
          Audit tests failed
        </span>
      )}

      {/* Empty model - only shown when status is known (not loading) and model is not computed */}
      {statusKnown && !isComputing && !firstError && !isComputed && (
        <span className="flex items-center gap-1.5 text-primary">
          <span className="material-symbols-outlined text-[16px]">rocket_launch</span>
          Start building your model in the Architecture page
        </span>
      )}

      {/* User Claim Write - Edit access management */}
      {claimWrite && (
        <UserClaimWriteIndicator {...claimWrite} />
      )}
    </div>
  );
}

function UserClaimWriteIndicator({
  userRights,
  userEditing,
  userClaimingWrite,
  claimWriteRequested,
  readOnlyReason,
  onClaimWrite,
  onGiveWrite,
}: UserClaimWriteProps) {
  const isCurrentUserEditing = userRights.right_to_write && userEditing.user_id === userRights.user_id;
  const anotherUserIsEditing = userEditing.user_id !== 0 && userEditing.user_id !== userRights.user_id;
  const someoneClaimsWrite = userClaimingWrite.user_id !== 0;

  return (
    <div className="flex items-center gap-3 ml-auto">
      {/* Someone is requesting write access from the current editor */}
      {someoneClaimsWrite && isCurrentUserEditing && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1 animate-in fade-in">
          <span className="material-symbols-outlined text-[16px] text-amber-600">
            edit_notifications
          </span>
          <span className="text-amber-700">
            <strong>{userClaimingWrite.user_name}</strong> requests edit access
          </span>
          <TailwindButton
            variant="secondary"
            size="sm"
            icon="handshake"
            onClick={onGiveWrite}
            className="h-6 text-[11px] ml-1"
          >
            Grant
          </TailwindButton>
        </div>
      )}

      {/* Current user is editing */}
      {isCurrentUserEditing && !someoneClaimsWrite && (
        <span className="flex items-center gap-1.5 text-emerald-600">
          <span className="material-symbols-outlined text-[16px]">edit</span>
          You are editing
        </span>
      )}

      {/* Another user is editing - claim write button */}
      {anotherUserIsEditing && !claimWriteRequested && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-slate-500">
            <span className="material-symbols-outlined text-[16px]">person</span>
            <strong>{userEditing.user_name}</strong> is editing
          </span>
          <TailwindButton
            variant="secondary"
            size="sm"
            icon="edit"
            onClick={onClaimWrite}
            className="h-6 text-[11px]"
          >
            Request edit
          </TailwindButton>
        </div>
      )}

      {/* Claim write has been requested, waiting for response */}
      {anotherUserIsEditing && claimWriteRequested && (
        <span className="flex items-center gap-1.5 text-amber-600">
          <span className="material-symbols-outlined animate-pulse text-[16px]">
            hourglass_top
          </span>
          Request pending...
        </span>
      )}

      {/* Read-only mode (no one editing or user has no write rights) */}
      {!isCurrentUserEditing && !anotherUserIsEditing && !userRights.right_to_write && readOnlyReason && (
        <span className="flex items-center gap-1.5 text-orange-500">
          <span className="material-symbols-outlined text-[16px]">visibility</span>
          Read-only — {readOnlyReason}
        </span>
      )}
    </div>
  );
}
